import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

const IntakeSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order') || 'XX000000';
  const customerName = searchParams.get('name') || 'there';
  // Set when this page was reached because the intake form was ALREADY
  // completed — either a revisit of the email link, or a second tab that
  // tried to submit after another tab already had (both landed here, both
  // add ?already=1). A genuine fresh submission never sets it.
  const alreadySubmitted = searchParams.get('already') === '1';

  useEffect(() => {
    if (alreadySubmitted) return; // no celebration for a revisit — nothing just happened in this tab
    // Three celebratory bursts: a big centre burst, then two side cannons —
    // brand palette (purple family + a touch of green for "success").
    const colors = ['#7c3aed', '#8b5cf6', '#c4b5fd', '#22c55e', '#4a2691'];
    confetti({ particleCount: 140, spread: 100, origin: { y: 0.55 }, colors });
    const t1 = setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors }), 250);
    const t2 = setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors }), 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="success-page">
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .success-page {
          --purple: #7c3aed;
          --purple-deep: #6d28d9;
          --purple-mid: #8b5cf6;
          --purple-soft: #8a7fb0;
          --gradient: linear-gradient(135deg, #8b5cf6, #7c3aed);
          --text: #1a1130;
          --text-mid: #4b4560;
          --text-soft: #5b5470;
          --text-mute: #6b6580;
          --text-dim: #9691a8;
          --navy: #1a0c38;
          --navy-mid: #4a2691;
          --bg-white: #ffffff;
          --bg-lavender: #f6f4fd;
          --bg-faint: #efedf6;
          --green: #22c55e;
          --green-deep: #16a34a;
          --green-lite: #dcfce7;
        }

        .success-page {
          min-height: 100vh;
          font-family: 'Poppins', Arial, Helvetica, sans-serif;
          background: radial-gradient(1100px 620px at 50% -220px, var(--navy-mid) 0%, var(--navy) 42%, var(--bg-lavender) 100%);
          background-color: var(--bg-lavender);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 88px 24px 40px;
          position: relative;
          overflow: hidden;
        }

        .card {
          background: var(--bg-white);
          border-radius: 24px;
          box-shadow: 0 24px 60px rgba(26, 12, 56, 0.22);
          padding: 48px 40px;
          max-width: 480px;
          width: 100%;
          text-align: center;
          position: relative;
          z-index: 10;
        }

        /* Animated Checkmark */
        .checkmark-circle {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: var(--green-lite);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 28px;
          animation: scale-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        @keyframes scale-in {
          0% { transform: scale(0); }
          100% { transform: scale(1); }
        }

        .checkmark {
          width: 48px;
          height: 48px;
          stroke: var(--green-deep);
          stroke-width: 4;
          stroke-linecap: round;
          stroke-linejoin: round;
          fill: none;
        }
        .checkmark-path {
          stroke-dasharray: 60;
          stroke-dashoffset: 60;
          animation: draw-check 0.6s ease-out 0.3s forwards;
        }
        @keyframes draw-check {
          to { stroke-dashoffset: 0; }
        }

        .title {
          font-size: 27px;
          font-weight: 800;
          color: var(--text);
          letter-spacing: -0.5px;
          margin-bottom: 10px;
          animation: fade-up 0.5s ease-out 0.2s both;
        }
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .subtitle {
          font-size: 15px;
          font-weight: 400;
          color: var(--text-mute);
          margin-bottom: 30px;
          line-height: 1.6;
          animation: fade-up 0.5s ease-out 0.3s both;
        }

        /* Order ID Box - Large and Prominent */
        .order-box {
          background: var(--bg-lavender);
          border: 2px solid var(--purple);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 28px;
          animation: fade-up 0.5s ease-out 0.4s both;
        }
        .order-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 8px;
        }
        .order-id {
          font-size: 34px;
          font-weight: 800;
          color: var(--purple);
          letter-spacing: 1px;
          font-family: 'Poppins', Arial, Helvetica, sans-serif;
        }
        .order-hint {
          font-size: 12px;
          color: var(--text-dim);
          margin-top: 10px;
        }

        /* Timeline */
        .timeline {
          text-align: left;
          margin-bottom: 28px;
          animation: fade-up 0.5s ease-out 0.5s both;
        }
        .timeline-title {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 16px;
          text-align: center;
        }
        .timeline-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 0;
        }
        .timeline-dot {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: var(--gradient);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .timeline-dot.upcoming {
          background: var(--bg-faint);
          color: var(--purple-soft);
        }
        .timeline-dot.done {
          background: var(--green);
          color: #ffffff;
        }
        .timeline-content {
          flex: 1;
        }
        .timeline-time {
          font-size: 13px;
          font-weight: 700;
          color: var(--text);
        }
        .timeline-desc {
          font-size: 12px;
          color: var(--text-mute);
          margin-top: 2px;
          line-height: 1.5;
        }

        /* Info Box */
        .info-box {
          background: var(--bg-lavender);
          border: 1px solid var(--bg-faint);
          border-radius: 12px;
          padding: 14px 18px;
          font-size: 13px;
          color: var(--text-soft);
          line-height: 1.55;
          animation: fade-up 0.5s ease-out 0.6s both;
        }
        .info-box strong {
          color: var(--text);
        }

        /* CTA button */
        .cta-btn {
          margin-top: 24px;
          width: 100%;
          padding: 14px 20px;
          border-radius: 13px;
          border: none;
          background: var(--gradient);
          color: #ffffff;
          font-family: 'Poppins', Arial, Helvetica, sans-serif;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 12px 28px rgba(124, 58, 237, 0.35);
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 16px 34px rgba(124, 58, 237, 0.45);
        }

        /* Footer */
        .footer {
          margin-top: 26px;
          font-size: 12px;
          color: var(--text-dim);
          animation: fade-up 0.5s ease-out 0.7s both;
        }
        .footer a {
          color: var(--purple);
          font-weight: 600;
          text-decoration: none;
        }
        .footer a:hover {
          text-decoration: underline;
        }

        /* Logo */
        .logo-wrap {
          position: absolute;
          top: 28px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 20;
          background: #ffffff;
          border-radius: 12px;
          padding: 10px 16px;
          box-shadow: 0 10px 26px rgba(20, 8, 50, 0.3);
        }
        .logo-img {
          display: block;
          height: 22px;
          width: auto;
        }

        @media (max-width: 520px) {
          .success-page {
            padding-top: 84px;
          }
          .card {
            padding: 36px 24px;
          }
          .order-id {
            font-size: 26px;
          }
          .title {
            font-size: 23px;
          }
        }
      `}</style>

      <div className="logo-wrap" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
        <img src="/images/brand/successlane-logo-mark.png" alt="SuccessLane" className="logo-img" />
      </div>

      <div className="card">
        {/* Animated Checkmark */}
        <div className="checkmark-circle">
          <svg className="checkmark" viewBox="0 0 50 50">
            <path className="checkmark-path" d="M14 27 L22 35 L38 16" />
          </svg>
        </div>

        {alreadySubmitted ? (
          <>
            <h1 className="title">Already submitted, {customerName}.</h1>
            <p className="subtitle">
              We already have your intake form on file for order {orderId} — likely from the link in your
              confirmation email. There's nothing more to do here. You can safely close this window; we'll
              email your recruiter-rewritten profile straight to your inbox.
            </p>
          </>
        ) : (
          <>
            <h1 className="title">We're on it, {customerName}!</h1>
            <p className="subtitle">
              Your profile brief has been received. Our expert team is starting work on your LinkedIn rewrite right now.
            </p>
          </>
        )}

        {/* Large Order ID Box */}
        <div className="order-box" data-testid="order-id-box">
          <div className="order-label">Your Order ID</div>
          <div className="order-id" data-testid="order-id">{orderId}</div>
          <div className="order-hint">Save this for all future communication</div>
        </div>

        {/* Timeline */}
        <div className="timeline">
          <div className="timeline-title">What happens next</div>

          <div className="timeline-item">
            <div className="timeline-dot">1</div>
            <div className="timeline-content">
              <div className="timeline-time">Now</div>
              <div className="timeline-desc">Your brief is being reviewed by our expert</div>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-dot upcoming">2</div>
            <div className="timeline-content">
              <div className="timeline-time">Next few hours</div>
              <div className="timeline-desc">We rewrite your profile sections</div>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-dot upcoming">3</div>
            <div className="timeline-content">
              <div className="timeline-time">QA + Recruiter Review</div>
              <div className="timeline-desc">A recruiter from your industry certifies your profile</div>
            </div>
          </div>

          <div className="timeline-item">
            <div className="timeline-dot done">✓</div>
            <div className="timeline-content">
              <div className="timeline-time">Within 48 hours</div>
              <div className="timeline-desc">Delivered to your inbox as PDF</div>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="info-box">
          {alreadySubmitted ? (
            <><strong>No action needed.</strong><br/>Your original confirmation email has your order details — we'll reach out there as your rewrite progresses.</>
          ) : (
            <><strong>Confirmation email sent!</strong><br/>Check your inbox for order details and tracking information.</>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="cta-btn"
          data-testid="back-to-home"
        >
          Back to home
        </button>

        <div className="footer">
          Questions? Contact <a href="mailto:contact@successlane.io">contact@successlane.io</a>
        </div>
      </div>
    </div>
  );
};

export default IntakeSuccessPage;
